"use client";

import { Box, BoxProps } from "@mui/material";
import { FC } from "react";

export interface VariantProps {
  imageSrc: string;
  alt?: string;
  cssProps?: BoxProps["sx"];
  // Image-specific styling props
  imageStyles?: BoxProps["sx"];
  borderRadius?: string | number;
  objectFit?: "contain" | "cover" | "fill" | "scale-down" | "none";
  width?: string | number;
  height?: string | number;
  maxWidth?: string | number;
  maxHeight?: string | number;
  // Container styling
  containerStyles?: BoxProps["sx"];
  // Interactive props
  enableHover?: boolean;
  hoverScale?: number;
  // Shadow and effects
  dropShadow?: boolean;
  customShadow?: string;
}

const HeroImage: FC<VariantProps> = ({
  imageSrc,
  alt = "Hero image",
  cssProps,
  imageStyles,
  borderRadius,
  objectFit = "contain",
  width,
  height,
  maxWidth = "100%",
  maxHeight,
  containerStyles,
  enableHover = false,
  hoverScale = 1.02,
  dropShadow = false,
  customShadow,
}) => {
  // Build dynamic styles for the image with responsive handling
  const dynamicImageStyles: BoxProps["sx"] = {
    maxWidth,
    height: height || "auto",
    width: width || "auto",
    maxHeight,
    objectFit: objectFit as any, // Fix type issue with objectFit
    borderRadius,
    transition: enableHover ? "transform 0.3s ease" : "none",
    "&:hover": enableHover
      ? {
          transform: `scale(${hoverScale})`,
        }
      : {},
    filter: dropShadow ? customShadow || "drop-shadow(0 8px 32px rgba(0, 0, 0, 0.2))" : "none",
    // Enhanced responsive handling
    ...(typeof height === "string" &&
      height.includes("px") && {
        height: {
          mobile: `${parseInt(height) * 0.7}px`, // 70% height on mobile
          tablet: `${parseInt(height) * 0.85}px`, // 85% height on tablet
          laptop: height, // Full height on laptop+
        },
      }),
    // Responsive border radius
    ...(borderRadius && {
      borderRadius: {
        mobile: typeof borderRadius === "number" ? borderRadius * 0.7 : borderRadius,
        tablet: typeof borderRadius === "number" ? borderRadius * 0.85 : borderRadius,
        laptop: borderRadius,
      },
    }),
    ...imageStyles,
  };

  // Build container styles with responsive enhancements
  const dynamicContainerStyles = {
    display: "flex" as const,
    alignItems: "center" as const,
    justifyContent: "center" as const,
    width: "100%",
    height: "auto",
    // Responsive padding for better mobile experience
    padding: {
      mobile: 1,
      tablet: 2,
      laptop: 0,
    },
    ...containerStyles,
    ...cssProps, // Keep backward compatibility
  };

  return (
    <Box sx={dynamicContainerStyles as any}>
      <Box component="img" src={imageSrc} alt={alt} loading="lazy" sx={dynamicImageStyles as any} />
    </Box>
  );
};

HeroImage.displayName = "HeroImage";

export default HeroImage;
